'use client'

import { Slot } from '@radix-ui/react-slot'
import * as React from 'react'

import { cn } from '@/lib/utils'

type ListProps = React.ComponentProps<'ul'> & {
  asChild?: boolean
  horizontal?: boolean
  /**
   * Densidade por número de linhas do DS (`.br-list[data-one-line]` e afins):
   * fixa a altura das linhas em 2em/4em/6em e tira o padding vertical delas.
   */
  lines?: 'one' | 'two' | 'three'
}

const linesHeight: Record<NonNullable<ListProps['lines']>, string> = {
  one: '[&_[data-slot=item]]:h-[2em]',
  two: '[&_[data-slot=item]]:h-[4em]',
  three: '[&_[data-slot=item]]:h-[6em]',
}

function List({ className, asChild = false, horizontal = false, lines, ...props }: ListProps) {
  const Comp = asChild ? Slot : 'ul'
  return (
    <Comp
      data-slot="list"
      data-orientation={horizontal ? 'horizontal' : 'vertical'}
      data-lines={lines}
      className={cn(
        'm-0 list-none bg-[var(--background)] p-0',
        // Passar o mouse na linha também destaca o botão que ela carrega.
        '[&_[data-slot=item]:hover_[data-slot=button]]:[background-image:linear-gradient(rgba(var(--interactive-rgb),var(--hover)),rgba(var(--interactive-rgb),var(--hover)))]',
        // Sublista: recolhe com transição e recua as linhas em
        // --spacing-scale-3x, como o DS faz com `.br-item[data-toggle] ~ .br-list`.
        '[&_[data-slot=list]]:overflow-hidden',
        '[&_[data-slot=list]]:transition-all [&_[data-slot=list]]:[transition-duration:400ms]',
        '[&_[data-slot=list]]:ease-in-out',
        '[&_[data-slot=list]_[data-slot=item]]:px-[var(--spacing-scale-3x)]',
        horizontal && [
          'flex flex-wrap items-stretch',
          // O cabeçalho ocupa a fileira inteira…
          '[&>[data-slot=list-header]]:w-full [&>[data-slot=list-header]]:grow',
          // …e as linhas dividem o espaço, com a altura mínima do DS.
          '[&>[data-slot=item]]:w-auto [&>[data-slot=item]]:flex-1',
          '[&>[data-slot=item]]:h-auto [&>[data-slot=item]]:min-h-[56px]',
        ],
        lines && [
          linesHeight[lines],
          '[&_[data-slot=item]]:overflow-hidden [&_[data-slot=item]]:py-0',
        ],
        className
      )}
      {...props}
    />
  )
}

function ListHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="list-header"
      className={cn(
        'flex items-center justify-between',
        '[font-size:var(--font-size-scale-base)] [font-weight:var(--font-weight-bold)]',
        'px-[var(--spacing-scale-2x)] py-[var(--spacing-scale-base)]',
        className
      )}
      {...props}
    />
  )
}

export { List, ListHeader }
